import { Component, Input } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidators, IConfirmModalConfig } from '@core';
import { BeneficiaireEffectifService } from '@core-lib/services/beneficiaireEffectif.service';
import { ConfirmationService } from '@core-lib/services/confirmation-service.service';
import { StoreLoader } from '@core';
import { Subject, of, takeUntil } from 'rxjs';

@Component({
    selector: 'beneficiaire-effectif-form',
    templateUrl: './beneficiaire-effectif-form.component.html',
    standalone: false
})
export class BeneficiaireEffectifFormComponent {
	destroySubscriptions$: Subject<boolean> = new Subject<boolean>();
	form: UntypedFormGroup;
	maxlengthsirenFieldInput: number = 9;
	maxLengthErrorMessage: string = 'ValidationErrors.WrongSirenLength';
	loader: StoreLoader = new StoreLoader(of(false), of(null));
	loaderSubject = new Subject<StoreLoader>();
	loader$ = this.loaderSubject.asObservable();

	get sirenField(): AbstractControl {
		return this.form?.get('siren');
	}

	constructor(
		private beneficiaireEffectifService: BeneficiaireEffectifService,
		private confirmationService: ConfirmationService
	) {}

	ngOnInit(): void {
		this.buildForm();
		this.loader$.pipe(takeUntil(this.destroySubscriptions$)).subscribe(res => (this.loader = res));

		this.sirenField.valueChanges.pipe(takeUntil(this.destroySubscriptions$)).subscribe(v => {
			if (v.trim().length === 11 && v.includes(' ')) {
				this.sirenField.markAsPristine();
			}
		});
	}

	buildForm(): void {
		this.form = new UntypedFormGroup({
			siren: new FormControl<string>(null, [Validators.required, CustomValidators.exactLength(this.maxlengthsirenFieldInput)])
		});
	}

	onSubmit(): void {
		this.loaderSubject.next(new StoreLoader(of(true), null));

		this.beneficiaireEffectifService.getRbePdfUrl(this.sirenField.value).subscribe({
			next: result => {
				if (result.isError) {
					this.openNoResultModal(result.detail);
				} else {
					const objectUrl = this.base64ToObjectUrl(result.rbeContent, 'application/pdf');
					this.openConfirmationModal(objectUrl);
				}

				this.loaderSubject.next(new StoreLoader(of(false), null));
			},
			error: error => {
				this.openErrorModal(error.translated);
				this.loaderSubject.next(new StoreLoader(of(false), of(error)));
			}
		});
	}

	openNoResultModal(detail: string): void {
		const config: IConfirmModalConfig = {
			canClose: false,
			textsKey: `BenefEffectifNoResult`,
			confirmBtnText: 'Actions.Close',
			bodyParams: {
				detail: detail
			}
		};
		this.confirmationService.openConfirmationModal(config);
	}

	openErrorModal(error): void {
		const config: IConfirmModalConfig = {
			canClose: false,
			textsKey: `BenefEffectifError`,
			confirmBtnText: 'Actions.Close',
			bodyParams: {
				message: error
			}
		};
		this.confirmationService.openConfirmationModal(config);
	}

	openConfirmationModal(fileURL: string): void {
		const config: IConfirmModalConfig = {
			canClose: false,
			textsKey: 'EditionConfirm',
			confirmBtnText: 'Actions.Ok',
			modalOptions: {
				class: 'modal-md'
			}
		};

		this.confirmationService.openConfirmationModal(config, () => this.onConfirm(fileURL));
	}

	onConfirm(fileURL: string): void {
		const link = document.createElement('a');
		link.href = fileURL;
		link.target = '_blank';
		link.rel = 'noopener';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	private base64ToObjectUrl (base64, mimeType) {
		const cleanedBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

		const byteCharacters = atob(cleanedBase64);
		const byteNumbers = new Array(byteCharacters.length);

		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: mimeType });

		return URL.createObjectURL(blob);
	}
}
